
import React, { useState } from 'react';
import { Project } from '@/types/supabase';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ExternalLink, UserCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface ProjectFeedbackFormProps {
  project: Project;
  onFeedbackSubmitted: () => void;
}

// Extending the Project type to include the joined user_profiles data
interface ProjectWithUserProfile extends Project {
  user_profiles?: {
    first_name: string | null;
    last_name: string | null;
  };
}

const ProjectFeedbackForm: React.FC<ProjectFeedbackFormProps> = ({
  project,
  onFeedbackSubmitted
}) => {
  const { toast } = useToast();
  const [feedback, setFeedback] = useState(project.feedback || '');
  const [score, setScore] = useState<number | null>(project.score || null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cast to the extended type
  const projectWithProfile = project as unknown as ProjectWithUserProfile;

  const isReviewed = project.score !== null;

  const handleScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (isNaN(value)) {
      setScore(null);
    } else {
      setScore(Math.min(Math.max(value, 0), 100));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!score) {
      toast({
        title: "Score required",
        description: "Please provide a score between 0 and 100.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const { error } = await supabase
        .from('projects')
        .update({ 
          feedback, 
          score,
          updated_at: new Date().toISOString()
        })
        .eq('id', project.id);
      
      if (error) throw error;
      
      toast({
        title: "Feedback submitted",
        description: `Feedback for "${project.title}" has been saved.`,
      });
      
      onFeedbackSubmitted();
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      toast({
        title: "Submission failed",
        description: error.message || "Failed to submit feedback.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>{project.title}</CardTitle>
            <CardDescription className="mt-1">
              <div className="flex items-center text-sm">
                <UserCircle className="h-4 w-4 mr-1" />
                {projectWithProfile.user_profiles?.first_name || ''} {projectWithProfile.user_profiles?.last_name || 'Unknown User'}
                <span className="mx-2">•</span>
                {project.updated_at && (
                  <span>{formatDistanceToNow(new Date(project.updated_at), { addSuffix: true })}</span>
                )}
              </div>
            </CardDescription>
          </div>
          {isReviewed && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              Reviewed
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {project.description && (
          <div>
            <h3 className="text-sm font-medium mb-1">Project Description</h3>
            <div className="text-sm text-muted-foreground">
              {project.description}
            </div>
          </div>
        )}
        
        {project.submission_url && (
          <div>
            <h3 className="text-sm font-medium mb-1">Submission</h3>
            <a 
              href={project.submission_url} 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
            >
              View submission <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="score">Score (0-100)</Label>
            <Input
              id="score"
              type="number"
              min="0"
              max="100"
              value={score === null ? '' : score}
              onChange={handleScoreChange}
              className="max-w-[120px]"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="feedback">Feedback</Label>
            <Textarea
              id="feedback"
              placeholder="Provide detailed feedback on the project..."
              rows={8}
              value={feedback || ''}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </div>
          
          <Button type="submit" disabled={isSubmitting} className="w-full">
            {isSubmitting ? 'Submitting...' : isReviewed ? 'Update Feedback' : 'Submit Feedback'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ProjectFeedbackForm;
